import { Dispatch, FC, ReactNode, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";


import SText from "@/styles/SText";
import CommonTable from "@/components/CommonTable";
import type { ColumnType } from "antd/es/table";
import { Flex, Input, InputNumber, Tag } from "antd";
import { MatchedRequirementColumn } from "..";
import { getRequirementRowKey, numFormmater } from "@/pages/Requirement/util";
import ListFilterBar from "@/components/ListFilterBar";
import { filterProductItemsByKeyword } from "@/utils/product";
import { MaterialSearchWrap } from "@/pages/Requirement/style";

interface PreOutDataProps {
    confirmProducts: Array<MatchedRequirementColumn>;
    scrollHeight?: number;
    confirmed?: boolean;
    toolbarExtra?: ReactNode;
    setMatchedProducts?: Dispatch<SetStateAction<MatchedRequirementColumn[]>>
}

const PreOutData: FC<PreOutDataProps> = ({
    confirmProducts,
    scrollHeight = 600,
    confirmed = false,
    toolbarExtra,
    setMatchedProducts,
}) => {
    const { t } = useTranslation();
    const [keyword, setKeyword] = useState("");
    const [searchKeyword, setSearchKeyword] = useState("");
    const filteredItems = useMemo(() => filterProductItemsByKeyword(confirmProducts, searchKeyword), [confirmProducts, searchKeyword]);
    const columns: ColumnType<MatchedRequirementColumn>[] = [
        {
            key: "sequence",
            title: <SText text={t("purchase.seq")} />,
            width: 80,
            fixed: "left",
            render: (_, record, index) => index + 1,
        },
        {
            key: "productCode",
            title: <SText text={t("product.code")} />,
            dataIndex: "code",
            width: 160,
            fixed: "left",
            render(value, row, rowIndex) {
                return <SText text={row?.product?.code || ''} />
            },
        },
        {
            key: "productName",
            title: <SText text={t("product.name")} />,
            dataIndex: "name",
            width: 180,
            fixed: "left",
            render(value, row, rowIndex) {
                return <SText text={row?.product?.name || ''} />
            },
        },
        {
            key: "model",
            title: <SText text={t("order.brand")} />,
            width: 340,
            fixed: "left",
            render(value, row, rowIndex) {
                return <SText text={row?.product?.model || ''} />
            },
        },

        {
            key: "uom",
            title: <SText text={t("product.uom")} />,
            width: 160,
            fixed: "left",
            render(value, row, rowIndex) {
                return <SText text={row?.product?.uom || ''} />
            },
        },
        {
            key: "preReady",
            title: <SText text={t("product.preReady")} />,
            width: 120,
            render(value, row, rowIndex) {
                return (row?.product?.preReady || 0) === 1 ? <Tag color='#87d068' >{t('product.isTrue')}</Tag> : <Tag color='#f50'>{t('product.isFalse')}</Tag>
            },
        },
        {
            key: "useAmount",
            title: <SText text={t("requirement.requireCount")} />,
            dataIndex: "useAmount",
            width: 160,
            hidden: !confirmed,
            render(value, row, rowIndex) {
                return <SText text={numFormmater(Number(row?.useAmount || ''))} />
            },
        },
        {
            key: "bomAmount",
            title: <SText text={t("requirement.bomAmount")} />,
            dataIndex: "bomAmount",
            width: 160,
            hidden: !confirmed,
            render(value, row, rowIndex) {
                return <SText text={numFormmater(Number(row?.baseQuantity || ''))} />
            },
        },
        {
            key: "useFactoryCount",
            title: <SText text={t("bom.useFactoryCount")} />,
            width: 160,
            hidden: !confirmed,
            render(value, row, rowIndex) {
                return <SText text={numFormmater(Number(row?.useFactoryCount || ''))} />
            },
        },
        {
            key: "preOutNum",
            title: <SText text={t("bom.preOutNum_1")} />,
            dataIndex: "preOutNum",
            width: 160,
            render(value, row, rowIndex) {
                return <SText text={!!row.preOutNum ? numFormmater(Number(row.preOutNum)) : ''} />
            },
        },
        {
            key: "purCount",
            title: <SText text={t("bom.prePurNum_1")} />,
            dataIndex: "purCount",
            width: 160,
            hidden: !confirmed,
            render(value, row, rowIndex) {
                return (row?.product?.preReady || 0) === 1 ? <InputNumber changeOnWheel formatter={val => numFormmater(Number(val || 0))}
                    value={row.purCount} max={row.useAmount} onChange={val => setMatchedProducts && setMatchedProducts(
                        confirmProducts.map(item => ({ ...item, purCount: row?.product?.id === item.product.id ? (val ?? 0) : item.purCount }))
                    )} /> : <SText text={numFormmater(Number(value || 0))} />
            },
        },
    ]

    return (
        <Flex vertical gap={8} style={{ width: "100%", height: "100%", marginTop: '6px' }}>
            <Flex gap={8} align="center">
                {toolbarExtra}
                <MaterialSearchWrap>
                    <ListFilterBar onSearch={() => setSearchKeyword(keyword)} onReset={() => { setKeyword(""); setSearchKeyword(""); }} showReset={false}>
                        {({ submit }) => (
                            <Input allowClear value={keyword} onChange={event => setKeyword(event.target.value)} onPressEnter={submit}
                                placeholder={`${t("product.code")} / ${t("product.name")} / ${t("product.model")}`}
                                aria-label={t("requirement.searchPro")} />
                        )}
                    </ListFilterBar>
                </MaterialSearchWrap>
            </Flex>
            <CommonTable rowKey={row => row.matchKey || getRequirementRowKey(row)} columns={columns} dataSource={filteredItems} scrollHeight={scrollHeight - 80} />
        </Flex>
    );
};

export default PreOutData;
